// vendor
import { Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
// services
import { AuthService } from '../../services/auth.service';
import { NotificationService } from '../../services/notification.service';
import { UsernameValidator } from '../../feature-modules/shared/form-validators/username-validator';
import { validationPatterns } from '../../config/validation.config';
import { filter, take, takeUntil } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { CookieService } from 'ngx-cookie-service';
import { Subject } from 'rxjs';
import { FormErrorService } from '../../feature-modules/shared/services/form-error.service';
import { WindowRefService } from '../../services/window-ref.service';
import { RouteWatcherService } from '../../feature-modules/shared/services/route-watcher.service';
import { EventTrackingService } from '../../services/event-tracking.service';
import { AuthFormName, AuthLinkType, AuthFormFieldName } from 'src/app/enums/event/event-data.enum';
import { AuthEventType } from 'src/app/enums/event/event.enum';
import { FormSubmit, LoginError, FieldError } from 'src/app/models/event-data.model';
import { FormTracking, FormTrackingConfig } from 'src/app/config/eev-form-tracking.config';
import { AccountsService } from '../../feature-modules/accounts/services/accounts.service';
import { resolveDeepLinkDestination } from '../../config/deep-link-destinations';

interface LoginPageViewEvent {
  eventName: string;
  marketingChannelName?: string;
}

interface MobileRedirectObj {
  destination: string;
  id: string;
}

@Component({
    encapsulation: ViewEncapsulation.None,
    selector: 'app-login',
    templateUrl: './login.component.html',
    styleUrls: ['./login.component.scss'],
    standalone: false
})
export class LoginComponent implements OnInit, OnDestroy {
  mobileRedirectObj: MobileRedirectObj;
  deepLinkUrl: string | null = null;
  isLoading = false;
  resumeApplicationUrl = environment.resumeApplicationUrl;
  usernameError = '';
  passwordError = '';
  currentUrl$ = this.routeWatcherService.currentUrl$;
  public callbackDomain: string;
  public whitespaceFilter = {
    y: { pattern: validationPatterns.whitespaceRegExp },
  };

  private readonly ngUnsubscribe$: Subject<boolean> = new Subject();

  public formTrackingConfig: FormTrackingConfig = FormTracking[AuthFormName.LOGIN];
  public fieldErrors$: Subject<FieldError> = new Subject();

  signInForm: UntypedFormGroup = new UntypedFormGroup({
    username: new UntypedFormControl('', [
      Validators.required,
      Validators.minLength(6),
      Validators.maxLength(25),
      UsernameValidator.hasWhitespace,
      Validators.pattern(validationPatterns.usernameRegExp),
      UsernameValidator.hasEmoji,
    ]),
    password: new UntypedFormControl('', [Validators.required, Validators.minLength(8)]),
  });

  FormTrackingConfig = FormTracking[AuthFormName.LOGIN];
  AuthLinkType = AuthLinkType;
  AuthFormName = AuthFormName;
  AuthFormFieldName = AuthFormFieldName;

  constructor(
    private readonly router: Router,
    private readonly authService: AuthService,
    private readonly notification: NotificationService,
    private readonly activatedRoute: ActivatedRoute,
    private readonly cookieService: CookieService,
    private readonly formErrorService: FormErrorService,
    private readonly windowRefService: WindowRefService,
    private readonly routeWatcherService: RouteWatcherService,
    private readonly eventTrackingService: EventTrackingService
  ) {}

  login() {
    if (this.signInForm.invalid) {
      this.signInForm.markAllAsTouched();
      return;
    }
    this.isLoading = true;

    this.eventTrackingService.trackEvent<FormSubmit>(
      AuthEventType.FORM_SUBMIT,
      this.formTrackingConfig.formSubmitEvent
    );
    const username = this.signInForm.getRawValue().username;
    const password = this.signInForm.getRawValue().password;

    this.authService
      .login(username, password)
      .then((user: any) => {
        this.isLoading = false;
        this.clearNavCookies();

        // Deep link in-app routing
        if (this.deepLinkUrl) {
          this.router.navigateByUrl(this.deepLinkUrl);
          return;
        }

        this.router.navigate(['/redirect/products'], {
          queryParams: {
            callback: this.callbackDomain,
            id: this.mobileRedirectObj?.id || null,
            destination: this.mobileRedirectObj?.destination || null,
          },
        });
      })
      .catch((error: any) => {
        this.isLoading = false;
        if (error.name === 'UserNotConfirmedException') {
          this.router.navigateByUrl('/activate', {
            state: {
              username,
              retryPassword: password,
            },
          });
        } else if (error.name === 'PasswordResetRequiredException') {
          this.router.navigateByUrl('/loginHelp/forgotPassword', {
            state: {
              passwordStatus: 'Reset',
              notification: error,
            },
          });
        } else {
          this.notification.displayClientError(error);
          this.eventTrackingService.trackEvent<LoginError>(AuthEventType.LOGIN_ERROR, {
            authErrorMessage: error?.message ?? '',
          });
        }
      });
  }

  clearNavCookies() {
    this.cookieService.delete(`has_bestegg_loan`, '/', environment.mainDomain);
    this.cookieService.delete('has_bestegg_card', '/', environment.mainDomain);
  }

  sendLoginPageviewEventData(pageUrl) {
    const eventData: LoginPageViewEvent = {
      eventName: 'pageView',
    };
    if (pageUrl.includes('?')) {
      const paramString = pageUrl.split('?')[1];
      const urlParams = new URLSearchParams(paramString);
      eventData.marketingChannelName = urlParams.get('marketingChannelName')
        ? urlParams.get('marketingChannelName')
        : undefined;
    }

    this.eventTrackingService.trackPageViewEvent(eventData);
  }

  ngOnInit() {
    const params = this.activatedRoute.snapshot.queryParams;

    this.mobileRedirectObj = {
      destination: params?.destination,
      id: params?.id,
    };

    this.deepLinkUrl = resolveDeepLinkDestination(params?.dest);

    this.activatedRoute.queryParams
      .pipe(
        take(1),
        filter((params) => params.callback)
      )
      .subscribe((params) => {
        this.callbackDomain = params.callback.toLowerCase();
      });

    this.signInForm.controls['username'].statusChanges.pipe(takeUntil(this.ngUnsubscribe$)).subscribe(() => {
      this.usernameError = this.formErrorService.getUsernameInputError(this.signInForm.controls['username']);
    });
    this.signInForm.controls['password'].statusChanges.pipe(takeUntil(this.ngUnsubscribe$)).subscribe(() => {
      this.passwordError = this.formErrorService.getPasswordInputError(this.signInForm.controls['password']);
    });

    if (!environment.production) {
      console.log('testing SSO redirect in non prod..');
    }
    this.currentUrl$.pipe(take(1)).subscribe((url) => {
      setTimeout(() => {
        this.sendLoginPageviewEventData(url);
      }, 3000);
    });
  }

  ngOnDestroy() {
    this.ngUnsubscribe$.next(null);
    this.ngUnsubscribe$.complete();
  }
}
